import { notification } from 'antd'
import { isArray, unset } from 'lodash'
import moment, { isMoment } from 'moment'
import { getTranslate } from 'react-localize-redux'

import { isNonEmptyArray, isObject } from '.'
import { camelCaseToKebabCase } from './string'
import { store } from '..'
import { API_DATE } from './momentjs'

/** @enum {string} */
export const HttpMethods = Object.freeze({
  GET: 'GET',
  DELETE: 'DELETE',
  PATCH: 'PATCH',
  POST: 'POST'
})

/** @description This is the first step to apply a better abstraction */
export const HOST_NAME = '/'

/** USER API ERRORS */
export const DELETED_USER = 'DELETED_USER'
export const DUPLICATED_USER = 'DUPLICATED_USER'

/**
 * @deprecated
 */
export const getUrlParametersFromObject = urlObject => {
  let parametersAsString = ''

  if (urlObject) {
    const urlParameters = Object.keys(urlObject)

    urlParameters.forEach((propertyName, index) => {
      parametersAsString += getUrlPartWithLength(
        propertyName,
        urlObject[propertyName],
        index,
        urlParameters.length
      )
    })
  }

  return parametersAsString
}

export const makeQueryStringFromObject = queryParameters => {
  let query = ''

  if (typeof queryParameters === 'object' && queryParameters !== null) {
    const parametersName = Object.keys(queryParameters)
    const maxIndex = parametersName.length - 1

    let index = 0

    parametersName.forEach(name => {
      if (
        typeof queryParameters[name] !== 'undefined' &&
        queryParameters[name] !== null &&
        queryParameters[name] !== ''
      ) {
        const firstSymbol = getFirstSymbolByIndex(index, maxIndex, query)

        query += buildParameter(name, queryParameters[name], firstSymbol)
        index++
      }
    })
  }

  return query
}

const getFirstSymbolByIndex = (index, maxIndex, query) => {
  if (index === 0 || query === '') {
    return '?'
  }

  if (index <= maxIndex) {
    return '&'
  }

  return ''
}

const buildParameter = (name, value, firstSymbol) => {
  if (Array.isArray(value)) {
    let arrayValues = firstSymbol

    value.forEach((v, index) => {
      if (index === 0) {
        arrayValues += `${name}[]=${v}`
      } else {
        arrayValues += `&${name}[]=${v}`
      }
    })

    return arrayValues
  }

  return `${firstSymbol}${name}=${value}`
}

export const arrayToUrlParameters = (array, parameterName) => {
  let urlParameters = ''

  array.forEach((element, index) => {
    if (index !== 0) {
      urlParameters += '&'
    }

    urlParameters += `${parameterName}[]=${element}`
  })

  return urlParameters
}

export const objectToUrlParameters = (object, parameterName) => {
  let urlParameters = ''
  let index = 0

  for (const key in object) {
    if (object[key] === 0 || (isArray(object[key]) && isNonEmptyArray(object[key])) || (!isArray(object[key]) && object[key]) || object[key] === false) {
      if (index !== 0) {
        urlParameters += '&'
      }

      if (isArray(object[key])) {
        urlParameters += arrayToUrlParameters(object[key], `${parameterName}[${key}]`)
      } else if (isObject(object[key])) {
        urlParameters += objectToUrlParameters(object[key], `${parameterName}[${key}]`)
      } else {
        urlParameters += `${parameterName}[${key}]=${object[key]}`
      }

      index++
    }
  }

  return urlParameters
}

const valueToUrlParameters = (name, value) => {
  return name + '=' + value
}

const getUrlPartWithLength = (propertyName, propertyValue, currentIndex, propertiesLength) => {
  const parameterAsString = typeof propertyValue === 'object'
    ? arrayToUrlParameters(propertyValue, propertyName)
    : valueToUrlParameters(propertyName, propertyValue)

  return currentIndex === (propertiesLength - 1)
    ? parameterAsString
    : parameterAsString + '&'
}

export const dispatchIfData = (dispatch, type, json) => {
  if (json && json.data) {
    dispatch({ type, payload: json.data })
  }
}

export const makeQueryString = parameters => {
  let queryString = ''

  if (isObject(parameters)) {
    let index = 0

    Object.keys(parameters).forEach(key => {
      const value = parameters[key]

      if (typeof value !== 'undefined' && value !== '') {
        let encodedParameter = index === 0 ? '?' : '&'

        if (isMoment(value)) {
          encodedParameter += camelCaseToKebabCase(key) + '=' + value.format(API_DATE)
        } else if (isObject(value)) {
          encodedParameter += objectToUrlParameters(value, camelCaseToKebabCase(key))
        } else if (isArray(value)) {
          encodedParameter += arrayToUrlParameters(value, camelCaseToKebabCase(key))
        } else {
          encodedParameter += camelCaseToKebabCase(key) + '=' + value
        }

        queryString += encodedParameter
        index++
      }
    })
  }

  return queryString
}

export const purgeMetadata = metadata => {
  if (typeof metadata.per_page !== 'undefined') {
    metadata.perPage = metadata.per_page

    unset(metadata.per_page)
  }

  if (typeof metadata.total_pages !== 'undefined') {
    metadata.totalPages = metadata.total_pages

    unset(metadata.total_pages)
  }

  return metadata
}

export const onError = (message, placement = 'bottomRight') => {
  notification.error({ message, placement })
}

export const onConsoleError = (error, message, placement = 'bottomRight') => {
  if (message) {
    onError(message, placement)
  }

  console.error(error)
}

export const onInfo = (message, placement = 'bottomRight') => {
  notification.info({ message, placement })
}

export const onSuccess = (message, placement = 'bottomRight') => {
  notification.success({ message, placement })
}

export const onWarning = (message, placement = 'bottomRight') => {
  notification.warning({ message, placement })
}

export const URIEncode = uri => {
  if (typeof uri === 'string') {
    uri = uri.replace('+', '%2B')
  }

  return uri
}

export const URIDecode = uri => {
  if (typeof uri === 'string') {
    uri = uri.replace('%2B', '+')
  }

  return uri
}

export const getFrontDate = backendDateString => {
  return moment(backendDateString.split('+')[0])
}

export const getErrorBody = error => {
  return error.body.getReader().read().then(({ value }) => {
    const decoded = new TextDecoder('utf-8').decode(value)

    return JSON.parse(decoded)
  })
}

export const displayErrorMessage = (error, translate) => {
  getErrorBody(error).then(({ message }) => onError(translate(message.toLowerCase())))
}

export const displayError = (error, prefix) => {
  return error.body.getReader().read().then(errorDisplayer(prefix))
}

export const displayInfo = (error, prefix) => {
  return error.body.getReader().read().then(infoDisplayer(prefix))
}

const errorDisplayer = prefix => ({ value }) => {
  const t = getTranslate(store.getState().locale)
  const decoded = new TextDecoder('utf-8').decode(value)

  onError(t(`${prefix}.${JSON.parse(decoded).message.toLowerCase()}`))
}

const infoDisplayer = prefix => ({ value }) => {
  const t = getTranslate(store.getState().locale)
  const decoded = new TextDecoder('utf-8').decode(value)

  onInfo(t(`${prefix}.${JSON.parse(decoded).message.toLowerCase()}`))
}

export const resolvePromises = promises => {
  return Promise.all(promises)
}

export const conditionalPromise = (functionToCall, condition, defaultValue) => {
  return new Promise(resolve => resolve(condition ? functionToCall() : defaultValue))
}

export const getDataOrDefault = (defaultValue = {}) => json => json?.data ?? defaultValue
